import React, { useEffect, useRef, useState } from "react";

// Modules
import { useNavigate, useParams } from "react-router-dom";
import moment from "moment";
import Chart from "chart.js/auto";

// App
import { apiEndpoints, serverUrl } from "../../config";
import { getOnlineNode } from "../../core/getNode";
import PageHeader from "../../partials/pageHeader";
import Error from "../../partials/error";

// UI components
import { Col, Container, Row } from "react-bootstrap";
import {
  Button,
  Menu,
  MenuItem,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/EditOutlined";
import DownloadIcon from "@mui/icons-material/FileDownloadOutlined";
import VerifiedIcon from "@mui/icons-material/VerifiedOutlined";
import CompanyappLoader from "../../partials/companyappLoader";

const eventLabels = {
  room_opened: "Room opened",
  content_viewed: "Content viewed",
  content_downloaded: "Downloaded",
  link_clicked: "Link clicked",
  page_viewed: "Page viewed",
};

const formatDuration = (seconds) => {
  if (!seconds) {
    return "-";
  }
  if (seconds < 60) {
    return `${seconds}s`;
  }
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) {
    return `${minutes}m ${seconds % 60}s`;
  }
  return `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
};

export default function SalesRoomDetail() {
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const [errorStatus, setErrorStatus] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [room, setRoom] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [days, setDays] = useState(30);

  const { id } = useParams();
  const navigate = useNavigate();
  const chartCanvasRef = useRef(null);
  const chartRef = useRef(null);
  const [exportAnchor, setExportAnchor] = useState(null);

  const exportCsv = (type) => {
    setExportAnchor(null);
    window.open(
      `${serverUrl}/api/v3/sales-room/${id}/analytics/export?type=${type}&days=${days}`,
      "_blank"
    );
  };

  useEffect(() => {
    getContent();
    // eslint-disable-next-line
  }, [id, days]);

  // Render the daily activity chart whenever analytics change.
  useEffect(() => {
    if (!analytics || !chartCanvasRef.current) {
      return;
    }

    if (chartRef.current) {
      chartRef.current.destroy();
    }

    chartRef.current = new Chart(chartCanvasRef.current, {
      type: "bar",
      data: {
        labels: analytics.daily.map((day) => day.day),
        datasets: [
          {
            label: "Room opens",
            data: analytics.daily.map((day) => day.opens),
            backgroundColor: "#0e6ba8",
          },
          {
            label: "Content views",
            data: analytics.daily.map((day) => day.views),
            backgroundColor: "#7fb3d5",
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          x: { stacked: true },
          y: { stacked: true, beginAtZero: true, ticks: { precision: 0 } },
        },
      },
    });

    return () => {
      if (chartRef.current) {
        chartRef.current.destroy();
        chartRef.current = null;
      }
    };
  }, [analytics]);

  const getContent = () => {
    setIsLoading(true);

    Promise.all([
      getOnlineNode(`${apiEndpoints.salesRooms}/${id}?_format=json`),
      getOnlineNode(
        `${apiEndpoints.salesRooms}/${id}/analytics?_format=json&days=${days}`
      ),
    ])
      .then(([roomResponse, analyticsResponse]) => {
        setRoom(roomResponse.data);
        setAnalytics(analyticsResponse.data);
        setIsLoading(false);
      })
      .catch((_error) => {
        setIsError(true);
        setErrorStatus(_error.response?.status || 0);
        setErrorMessage(
          _error.response?.statusText || "Failed to load analytics"
        );
        setIsLoading(false);
      });
  };

  if (isError) {
    return <Error status={errorStatus} message={errorMessage} />;
  }

  const totals = analytics?.totals;

  const statCards = totals
    ? [
        { label: "Room opens", value: totals.room_opened },
        { label: "Unique visitors", value: totals.unique_visitors },
        { label: "Content views", value: totals.content_viewed },
        { label: "Downloads", value: totals.content_downloaded },
        { label: "Time spent", value: formatDuration(totals.total_duration) },
        {
          label: "Last activity",
          value: totals.last_activity
            ? moment.unix(totals.last_activity).fromNow()
            : "-",
        },
      ]
    : [];

  return (
    <main className="sales-room-detail sales-rooms screen">
      <PageHeader pageName={room ? room.title : "Sales Room"} filters={false} />
      {isLoading ? (
        <CompanyappLoader />
      ) : (
        <Container className="px-0 mt-3">
          <Row className="mb-3 align-items-center">
            <Col xs="auto">
              <ToggleButtonGroup
                exclusive
                size="small"
                value={days}
                onChange={(e, value) => {
                  if (value !== null) {
                    setDays(value);
                  }
                }}
              >
                <ToggleButton value={7}>7 days</ToggleButton>
                <ToggleButton value={30}>30 days</ToggleButton>
                <ToggleButton value={90}>90 days</ToggleButton>
                <ToggleButton value={0}>All time</ToggleButton>
              </ToggleButtonGroup>
            </Col>
            <Col xs="auto" className="ms-auto d-flex gap-2">
              <Button
                variant="outlined"
                className="cancel"
                sx={{
                  backgroundColor: "primary.contrastText",
                  textTransform: "none",
                }}
                startIcon={<DownloadIcon />}
                onClick={(e) => setExportAnchor(e.currentTarget)}
              >
                Export CSV
              </Button>
              <Menu
                anchorEl={exportAnchor}
                open={!!exportAnchor}
                onClose={() => setExportAnchor(null)}
              >
                <MenuItem onClick={() => exportCsv("events")}>
                  Event timeline
                </MenuItem>
                <MenuItem onClick={() => exportCsv("content")}>
                  Content engagement
                </MenuItem>
                <MenuItem onClick={() => exportCsv("visitors")}>
                  Visitors &amp; contacts
                </MenuItem>
              </Menu>
              <Button
                variant="outlined"
                className="cancel"
                sx={{
                  backgroundColor: "primary.contrastText",
                  textTransform: "none",
                }}
                startIcon={<EditIcon />}
                onClick={() => navigate(`/sales-rooms/edit/${id}`)}
              >
                Edit room
              </Button>
            </Col>
          </Row>

          <Row className="sales-room-detail__stats gy-3 mb-4">
            {statCards.map((stat) => (
              <Col xs={6} md={4} lg={2} key={stat.label}>
                <div className="stat-card">
                  <Typography owner="admin" variant="h4">
                    {stat.value}
                  </Typography>
                  <Typography owner="admin" variant="body2">
                    {stat.label}
                  </Typography>
                </div>
              </Col>
            ))}
          </Row>

          {analytics?.daily?.length > 0 && (
            <Row className="mb-4">
              <Col xs={12}>
                <Typography owner="admin" variant="h6" className="mb-2">
                  Activity
                </Typography>
                <div className="sales-room-detail__chart">
                  <canvas ref={chartCanvasRef} />
                </div>
              </Col>
            </Row>
          )}

          <Row className="mb-4">
            <Col xs={12} lg={7}>
              <Typography owner="admin" variant="h6" className="mb-2">
                Content engagement
              </Typography>
              {analytics?.content?.length ? (
                <div className="sales-room-detail__table">
                  <Row className="table-head gx-2 py-1">
                    <Col xs={5}>
                      <Typography owner="admin" variant="subtitle2">
                        Content
                      </Typography>
                    </Col>
                    <Col xs={2} className="text-center">
                      <Typography owner="admin" variant="subtitle2">
                        Views
                      </Typography>
                    </Col>
                    <Col xs={2} className="text-center">
                      <Typography owner="admin" variant="subtitle2">
                        Downloads
                      </Typography>
                    </Col>
                    <Col xs={3} className="text-center">
                      <Typography owner="admin" variant="subtitle2">
                        Avg. time
                      </Typography>
                    </Col>
                  </Row>
                  {analytics.content.map((row) => (
                    <Row
                      className="gx-2 py-1 table-row"
                      key={`${row.content_type}-${row.content_id}`}
                    >
                      <Col xs={5}>
                        <Typography owner="admin" variant="body2">
                          {row.title || `${row.content_type} ${row.content_id}`}
                        </Typography>
                        <Typography
                          owner="admin"
                          variant="subtitle2"
                          className="table-row__type"
                        >
                          {row.content_type}
                        </Typography>
                      </Col>
                      <Col xs={2} className="text-center">
                        <Typography owner="admin" variant="body2">
                          {row.views}
                        </Typography>
                      </Col>
                      <Col xs={2} className="text-center">
                        <Typography owner="admin" variant="body2">
                          {row.downloads + row.clicks}
                        </Typography>
                      </Col>
                      <Col xs={3} className="text-center">
                        <Typography owner="admin" variant="body2">
                          {formatDuration(row.avg_duration)}
                        </Typography>
                      </Col>
                    </Row>
                  ))}
                </div>
              ) : (
                <Typography owner="admin" variant="body2">
                  No content engagement yet.
                </Typography>
              )}
            </Col>
            <Col xs={12} lg={5}>
              <Typography owner="admin" variant="h6" className="mb-2">
                Visitors
              </Typography>
              {analytics?.contacts?.length > 0 && (
                <div className="sales-room-detail__table mb-3">
                  {analytics.contacts.map((contact) => (
                    <Row className="gx-2 py-1 table-row" key={contact.email}>
                      <Col xs={5}>
                        <Typography
                          owner="admin"
                          variant="body2"
                          className="d-flex align-items-center gap-1"
                        >
                          {contact.name ? (
                            <span>
                              <strong>{contact.name}</strong> ({contact.email})
                            </span>
                          ) : (
                            <strong>{contact.email}</strong>
                          )}
                          {contact.verified && (
                            <VerifiedIcon
                              sx={{ fontSize: "0.9rem" }}
                              color="success"
                              titleAccess="Email verified via access link"
                            />
                          )}
                        </Typography>
                        <Typography
                          owner="admin"
                          variant="subtitle2"
                          className="table-row__type"
                        >
                          last seen {moment.unix(contact.last_seen).fromNow()}
                          {contact.devices > 1 ? ` · ${contact.devices} devices` : ""}
                        </Typography>
                      </Col>
                      <Col xs={3} className="text-center">
                        <Typography owner="admin" variant="body2">
                          {contact.events} events
                        </Typography>
                      </Col>
                      <Col xs={4} className="text-center">
                        <Typography owner="admin" variant="body2">
                          {formatDuration(contact.total_duration)}
                        </Typography>
                      </Col>
                    </Row>
                  ))}
                </div>
              )}
              {analytics?.visitors?.filter((v) => !v.email).length > 0 && (
                <div className="sales-room-detail__table">
                  {analytics.visitors
                    .filter((v) => !v.email)
                    .map((visitor, index) => (
                      <Row
                        className="gx-2 py-1 table-row"
                        key={visitor.visitor_id}
                      >
                        <Col xs={5}>
                          <Typography owner="admin" variant="body2">
                            Anonymous visitor {index + 1}
                          </Typography>
                          <Typography
                            owner="admin"
                            variant="subtitle2"
                            className="table-row__type"
                          >
                            first seen {moment.unix(visitor.first_seen).fromNow()}
                          </Typography>
                        </Col>
                        <Col xs={3} className="text-center">
                          <Typography owner="admin" variant="body2">
                            {visitor.events} events
                          </Typography>
                        </Col>
                        <Col xs={4} className="text-center">
                          <Typography owner="admin" variant="body2">
                            {formatDuration(visitor.total_duration)}
                          </Typography>
                        </Col>
                      </Row>
                    ))}
                </div>
              )}
              {!analytics?.visitors?.length && (
                <Typography owner="admin" variant="body2">
                  No visitors yet - share the room link to get started.
                </Typography>
              )}
            </Col>
          </Row>

          <Row className="mb-4">
            <Col xs={12}>
              <Typography owner="admin" variant="h6" className="mb-2">
                Timeline
              </Typography>
              {analytics?.timeline?.rows?.length ? (
                <div className="sales-room-detail__timeline">
                  {analytics.timeline.rows.map((event, index) => (
                    <Row className="gx-2 py-1 table-row" key={`event-${index}`}>
                      <Col xs="auto">
                        <Typography owner="admin" variant="subtitle2">
                          {moment.unix(event.created).format("DD MMM HH:mm")}
                        </Typography>
                      </Col>
                      <Col>
                        <Typography owner="admin" variant="body2">
                          {eventLabels[event.event_type] || event.event_type}
                          {event.content_type
                            ? ` - ${event.content_type} ${event.content_id}`
                            : ""}
                          {event.duration
                            ? ` (${formatDuration(event.duration)})`
                            : ""}
                        </Typography>
                      </Col>
                    </Row>
                  ))}
                </div>
              ) : (
                <Typography owner="admin" variant="body2">
                  No activity in this period.
                </Typography>
              )}
            </Col>
          </Row>
        </Container>
      )}
    </main>
  );
}
